import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ImportSource } from "@/components/product/import/ImportSource";
import { ImportReview } from "@/components/product/import/ImportReview";
import { ImportGroups } from "@/components/product/import/ImportGroups";
import { itemError } from "@/components/product/import/ImportItemEdit";

function buildPayload(preview, sel, targets) {
  const out = new Map();
  preview.categories.forEach((c) => c.items.filter((i) => sel.has(i.key)).forEach((i) => {
    const name = (i.cat || c.name).trim();
    const extra = preview.groups.filter((g) => targets[g.key] === c.key || targets[g.key] === "all")
      .map(({ name: n, kind, options }) => ({ name: n, kind, required: false, min: 0, max: null, options }));
    if (!out.has(name.toLowerCase())) out.set(name.toLowerCase(), { name, items: [] });
    out.get(name.toLowerCase()).items.push({ name: i.name.trim(), description: i.description || "", price: i.price, groups: [...i.groups, ...extra].map((g) => ({ ...g, max: g.max || null })) });
  }));
  return [...out.values()];
}

export function MenuImportDialog({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(false);
  const loaded = (data) => {
    setPreview({ ...data, categories: data.categories.map((c) => ({ ...c, items: c.items.map((i) => ({ ...i, orig: i.name, cat: null })) })) });
    setSel(new Set(data.categories.flatMap((c) => c.items.filter((i) => !i.exists).map((i) => i.key))));
    setTargets(Object.fromEntries(data.groups.map((g) => [g.key, g.target])));
  };
  const read = async (fn) => {
    setBusy(true);
    try { loaded((await fn()).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const patchCats = (fn) => setPreview((p) => ({ ...p, categories: p.categories.map(fn) }));
  const editItem = (ck, ik, patch) => patchCats((c) => (c.key !== ck ? c : { ...c, items: c.items.map((i) => (i.key === ik ? { ...i, ...patch } : i)) }));
  const renameCat = (ck, name) => patchCats((c) => (c.key !== ck ? c : { ...c, name, items: c.items.map((i) => (i.cat && i.cat.toLowerCase() === name.toLowerCase() ? { ...i, cat: null } : i)) }));
  const bad = preview ? preview.categories.reduce((n, c) => n + c.items.filter((i) => sel.has(i.key) && itemError(i, c.name)).length, 0) : 0;
  const readFile = (file) => read(() => { const fd = new FormData(); fd.append("file", file); return api.post("/products/import/file", fd); });
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/products/import/commit", { categories: buildPayload(preview, sel, targets) });
      toast.success(`${data.products} produto(s) importado(s)${data.categories ? ` e ${data.categories} categoria(s) criada(s)` : ""}. Você pode editar qualquer um na lista.`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] grid-cols-[minmax(0,1fr)] overflow-y-auto" data-testid="menu-import-dialog">
        <DialogHeader>
          <DialogTitle>Importar cardápio</DialogTitle>
          <DialogDescription>{preview ? "Revise os itens encontrados e desmarque o que não quiser importar." : "Envie o PDF ou a foto do cardápio, ou cole o link do cardápio digital."}</DialogDescription>
        </DialogHeader>
        {!preview ? <ImportSource busy={busy} onFile={readFile} onUrl={(url) => read(() => api.post("/products/import/url", { url }))} /> : (
          <>
            <ImportReview preview={preview} sel={sel} setSel={setSel} onEdit={editItem} onRename={renameCat} />
            <ImportGroups groups={preview.groups} categories={preview.categories} targets={targets} setTargets={setTargets} />
            <div className="sticky -bottom-6 -mx-6 -mb-6 flex min-w-0 flex-wrap items-center gap-2 border-t bg-background px-6 py-4">
              <Button variant="ghost" onClick={() => setPreview(null)} disabled={busy} data-testid="menu-import-back-btn">Voltar</Button>
              <p className="flex-1 text-sm text-muted-foreground" data-testid="menu-import-selected-count">{sel.size} produto(s) selecionado(s){bad > 0 && <span className="block text-red-500" data-testid="menu-import-invalid-count">Corrija {bad} produto(s) marcado(s) em vermelho</span>}</p>
              <Button onClick={commit} disabled={busy || !sel.size || bad > 0} data-testid="menu-import-confirm-btn">{busy ? "Importando..." : `Importar ${sel.size} produto(s)`}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
